(() => {
class VelvetBoxBuilder {
  constructor(section) {
    this.section = section;
    this.minimumProducts = Number(section.dataset.minimumProducts) || 5;
    this.cartAddUrl = section.dataset.cartAddUrl || "/cart/add";
    this.checkoutUrl = section.dataset.checkoutUrl || "/checkout";
    this.boxName = section.dataset.boxName || "Velvet Custom Box";
    this.readyLabel = section.dataset.readyLabel || "Checkout Now";
    this.loadingLabel = section.dataset.loadingLabel || "Preparing Checkout...";

    this.products = Array.from(section.querySelectorAll("[data-box-product]"));
    this.slotsContainer = section.querySelector("[data-box-slots]");
    this.slots = Array.from(section.querySelectorAll("[data-box-slot]"));
    this.progress = section.querySelector("[data-box-progress]");
    this.progressBar = section.querySelector("[data-box-progress-bar]");
    this.progressMessage = section.querySelector("[data-box-progress-message]");
    this.emptyProgressMessage = this.progressMessage?.textContent?.trim() || "Start adding your favourites";
    this.count = section.querySelector("[data-box-count]");
    this.total = section.querySelector("[data-box-total]");
    this.status = section.querySelector("[data-box-status]");
    this.submitButton = section.querySelector("[data-box-submit]");
    this.submitLabel = section.querySelector("[data-box-submit-label]");
    this.mobileCart = section.querySelector("[data-box-mobile-cart]");
    this.mobileSubmitButton = section.querySelector("[data-box-mobile-submit]");
    this.mobileSubmitLabel = section.querySelector("[data-box-mobile-submit-label]");
    this.mobileCount = section.querySelector("[data-box-mobile-count]");
    this.resetButton = section.querySelector("[data-box-reset]");
    this.boxTarget = section.querySelector("[data-box-target]");
    this.items = [];
    this.isSubmitting = false;

    this.bindEvents();
    this.render();
  }

  bindEvents() {
    this.products.forEach((card) => {
      card.querySelector("[data-box-add]")?.addEventListener("click", () => this.addProduct(card));
      card.querySelector("[data-box-variant]")?.addEventListener("change", () => {
        this.updateVariantPrice(card);
        this.updateProductCounts();
      });
    });

    this.slotsContainer?.addEventListener("click", (event) => {
      const removeButton = event.target.closest("[data-box-remove]");
      if (!removeButton) return;
      this.removeItem(Number(removeButton.dataset.boxRemove));
    });

    this.resetButton?.addEventListener("click", () => this.reset());
    this.submitButton?.addEventListener("click", () => this.checkoutBox());
    this.mobileSubmitButton?.addEventListener("click", () => this.checkoutBox());
  }

  getSelectedProduct(card) {
    const variantSelect = card.querySelector("[data-box-variant]");
    const option = variantSelect?.selectedOptions?.[0];
    if (!option) return null;

    return {
      productId: Number(card.dataset.productId),
      variantId: Number(option.value),
      productTitle: card.dataset.productTitle || "Candy",
      variantTitle: option.dataset.variantTitle || "",
      price: Number(option.dataset.price) || 0,
      image: card.dataset.productImage || "",
    };
  }

  addProduct(card) {
    const item = this.getSelectedProduct(card);
    if (!item?.variantId) {
      this.setStatus("Please choose an available product option.", "error");
      return;
    }

    const sourceImage = card.querySelector(".velvet-box-builder__product-image");
    this.items.push(item);
    this.render();
    const targetSlot = this.slots[this.items.length - 1];
    if (this.slotsContainer) this.slotsContainer.scrollTop = this.slotsContainer.scrollHeight;
    this.animateToBox(sourceImage, targetSlot);
    this.setStatus(`${item.productTitle} added to your box.`, "success");
  }

  removeItem(index) {
    if (!Number.isInteger(index) || !this.items[index]) return;
    const [removed] = this.items.splice(index, 1);
    this.render();
    this.setStatus(`${removed.productTitle} removed from your box.`);
  }

  reset() {
    if (this.items.length === 0 || this.isSubmitting) return;
    this.items = [];
    this.render();
    this.setStatus("Your box is empty. Choose your favourites to start again.");
  }

  render() {
    const itemCount = this.items.length;
    const totalPrice = this.items.reduce((sum, item) => sum + item.price, 0);
    const remaining = Math.max(this.minimumProducts - itemCount, 0);
    const minimumReached = itemCount >= this.minimumProducts;

    this.syncSlots(itemCount);

    this.slots.forEach((slot, index) => {
      const item = this.items[index];
      slot.replaceChildren();
      slot.classList.toggle("is-empty", !item);
      slot.classList.toggle("is-filled", Boolean(item));

      if (!item) {
        const number = document.createElement("span");
        number.textContent = String(index + 1);
        slot.append(number);
        slot.setAttribute("aria-label", `Empty box spot ${index + 1}`);
        return;
      }

      if (item.image) {
        const image = document.createElement("img");
        image.src = item.image;
        image.alt = item.productTitle;
        image.loading = "lazy";
        slot.append(image);
      } else {
        const fallback = document.createElement("span");
        fallback.textContent = "🍬";
        fallback.setAttribute("aria-hidden", "true");
        slot.append(fallback);
      }

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "velvet-box-builder__slot-remove";
      remove.dataset.boxRemove = String(index);
      remove.setAttribute("aria-label", `Remove ${item.productTitle} from box`);
      remove.textContent = "×";
      slot.append(remove);
      slot.setAttribute("aria-label", `${item.productTitle}, box spot ${index + 1}`);
    });

    if (this.count) this.count.textContent = String(itemCount);
    if (this.total) this.total.textContent = this.formatPrice(totalPrice);
    if (this.progress) this.progress.setAttribute("aria-valuenow", String(Math.min(itemCount, this.minimumProducts)));
    if (this.progressBar) {
      this.progressBar.style.width = `${Math.min((itemCount / this.minimumProducts) * 100, 100)}%`;
    }

    if (this.progressMessage) {
      if (itemCount === 0) {
        this.progressMessage.textContent = this.emptyProgressMessage;
      } else if (minimumReached) {
        this.progressMessage.textContent = "Ready — add as many as you like";
      } else {
        this.progressMessage.textContent = `Add ${remaining} more ${remaining === 1 ? "product" : "products"}`;
      }
    }

    if (this.submitButton) this.submitButton.disabled = !minimumReached || this.isSubmitting;
    if (this.resetButton) this.resetButton.disabled = itemCount === 0 || this.isSubmitting;
    if (this.submitLabel && !this.isSubmitting) this.submitLabel.textContent = this.readyLabel;

    if (this.mobileCart) {
      const showMobileCart = itemCount > 0;
      this.mobileCart.classList.toggle("is-visible", showMobileCart);
      this.mobileCart.setAttribute("aria-hidden", String(!showMobileCart));
    }

    if (this.mobileSubmitButton) {
      this.mobileSubmitButton.disabled = !minimumReached || this.isSubmitting;
      this.mobileSubmitButton.tabIndex = itemCount > 0 ? 0 : -1;
    }

    if (this.mobileSubmitLabel && !this.isSubmitting) {
      this.mobileSubmitLabel.textContent = minimumReached
        ? this.readyLabel
        : `Add ${remaining} more ${remaining === 1 ? "product" : "products"}`;
    }

    if (this.mobileCount) {
      this.mobileCount.textContent = String(itemCount);
      this.mobileCount.setAttribute(
        "aria-label",
        `${itemCount} ${itemCount === 1 ? "product" : "products"} selected`
      );
    }

    this.updateProductCounts();
  }

  syncSlots(itemCount) {
    if (!this.slotsContainer) return;
    const desiredSlotCount = Math.max(this.minimumProducts, itemCount);

    while (this.slots.length < desiredSlotCount) {
      const slot = document.createElement("li");
      slot.className = "velvet-box-builder__slot is-empty";
      slot.dataset.boxSlot = "";
      this.slotsContainer.append(slot);
      this.slots.push(slot);
    }

    while (this.slots.length > desiredSlotCount) {
      this.slots.pop()?.remove();
    }
  }

  updateProductCounts() {
    this.products.forEach((card) => {
      const productId = Number(card.dataset.productId);
      const productCount = this.items.filter((item) => item.productId === productId).length;
      const countBadge = card.querySelector("[data-box-selected-count]");
      const addButton = card.querySelector("[data-box-add]");
      const selected = this.getSelectedProduct(card);

      if (countBadge) {
        countBadge.textContent = String(productCount);
        countBadge.hidden = productCount === 0;
      }

      if (addButton) {
        addButton.disabled = this.isSubmitting || !selected;
      }
    });
  }

  updateVariantPrice(card) {
    const option = card.querySelector("[data-box-variant]")?.selectedOptions?.[0];
    const price = card.querySelector("[data-box-price]");
    if (option && price) price.textContent = option.dataset.priceLabel || this.formatPrice(Number(option.dataset.price));
  }

  animateToBox(source, target) {
    if (!source || !target || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const sourceRect = source.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    if (!sourceRect.width || !sourceRect.height || !targetRect.width || !targetRect.height) return;

    const flyingImage = source.cloneNode(true);
    flyingImage.removeAttribute("loading");
    flyingImage.className = "velvet-box-builder__flying-image";
    flyingImage.style.left = `${sourceRect.left}px`;
    flyingImage.style.top = `${sourceRect.top}px`;
    flyingImage.style.width = `${sourceRect.width}px`;
    flyingImage.style.height = `${sourceRect.height}px`;
    document.body.append(flyingImage);

    const finalWidth = Math.min(targetRect.width * 0.72, 68);
    const finalHeight = Math.min(targetRect.height * 0.72, 68);
    const finalLeft = targetRect.left + (targetRect.width - finalWidth) / 2;
    const finalTop = targetRect.top + (targetRect.height - finalHeight) / 2;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        flyingImage.style.left = `${finalLeft}px`;
        flyingImage.style.top = `${finalTop}px`;
        flyingImage.style.width = `${finalWidth}px`;
        flyingImage.style.height = `${finalHeight}px`;
        flyingImage.style.opacity = "0.2";
        flyingImage.style.transform = "rotate(10deg) scale(0.72)";
      });
    });

    this.boxTarget?.classList.add("is-receiving");
    window.setTimeout(() => this.boxTarget?.classList.remove("is-receiving"), 300);
    window.setTimeout(() => flyingImage.remove(), 620);
  }

  formatPrice(cents) {
    if (typeof window.formatMoney === "function") {
      return window.formatMoney(cents, window.MinimogSettings?.money_format);
    }

    const currency = window.MinimogSettings?.currency_code || "USD";
    return new Intl.NumberFormat(document.documentElement.lang || "en", {
      style: "currency",
      currency,
    }).format(cents / 100);
  }

  setStatus(message, type = "") {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle("is-error", type === "error");
    this.status.classList.toggle("is-success", type === "success");
  }

  async checkoutBox() {
    if (this.isSubmitting || this.items.length < this.minimumProducts) return;

    this.isSubmitting = true;
    this.submitButton?.classList.add("is-loading");
    this.mobileSubmitButton?.classList.add("is-loading");
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute("aria-busy", "true");
    }
    if (this.mobileSubmitButton) {
      this.mobileSubmitButton.disabled = true;
      this.mobileSubmitButton.setAttribute("aria-busy", "true");
    }
    if (this.resetButton) this.resetButton.disabled = true;
    if (this.submitLabel) this.submitLabel.textContent = this.loadingLabel;
    if (this.mobileSubmitLabel) this.mobileSubmitLabel.textContent = this.loadingLabel;
    this.updateProductCounts();
    this.setStatus("Preparing your custom box for checkout...");

    const boxId = `${this.section.dataset.sectionId || "box"}-${Date.now().toString(36)}`;
    const groupedItems = new Map();

    this.items.forEach((item) => {
      const existing = groupedItems.get(item.variantId);
      if (existing) {
        existing.quantity += 1;
      } else {
        groupedItems.set(item.variantId, {
          id: item.variantId,
          quantity: 1,
          properties: {
            _velvet_box_id: boxId,
            _velvet_box_name: this.boxName,
          },
        });
      }
    });

    const endpoint = this.cartAddUrl.endsWith(".js") ? this.cartAddUrl : `${this.cartAddUrl}.js`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items: Array.from(groupedItems.values()) }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.description || result.message || "Your box could not be added to the cart.");
      }

      this.setStatus("Your custom box was added. Opening checkout...", "success");
      window.location.assign(this.checkoutUrl);
    } catch (error) {
      this.isSubmitting = false;
      this.submitButton?.classList.remove("is-loading");
      this.mobileSubmitButton?.classList.remove("is-loading");
      if (this.submitButton) {
        this.submitButton.removeAttribute("aria-busy");
      }
      if (this.mobileSubmitButton) {
        this.mobileSubmitButton.removeAttribute("aria-busy");
      }
      this.render();
      this.setStatus(error.message || "Your box could not be added. Please try again.", "error");
    }
  }
}

function initializeVelvetBoxBuilders(root = document) {
  const sections = [];
  if (root.matches?.("[data-box-builder]")) sections.push(root);
  root.querySelectorAll?.("[data-box-builder]").forEach((section) => sections.push(section));

  sections.forEach((section) => {
    if (section.dataset.boxBuilderInitialized === "true") return;
    section.dataset.boxBuilderInitialized = "true";
    section.velvetBoxBuilder = new VelvetBoxBuilder(section);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializeVelvetBoxBuilders());
} else {
  initializeVelvetBoxBuilders();
}

document.addEventListener("shopify:section:load", (event) => {
  initializeVelvetBoxBuilders(event.target);
});
})();
